import React, { useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../lib/i18n';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useLanguage();
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4843">
        <MarketplaceHeader data-id="element-4844" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-4845">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-4846" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-4847">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-4848" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-4849" />
            <Skeleton className="h-4 w-2/3" data-id="element-4850" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-4851">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-4852">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-4853" />
                <div className="flex-1" data-id="element-4854">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-4855" />
                  <Skeleton className="h-4 w-full" data-id="element-4856" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-4857" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4858">
        <MarketplaceHeader data-id="element-4859" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-4860">
          <EmptyState icon={Search} title={t('tracking_not_found')} description={t('error')} actionLabel={t('confirm_back')} onAction={() => window.location.href = '/'} data-id="element-4861" />
        </main>
        <Footer data-id="element-4862" />
      </div>;
  }
  const trackingUrl = window.location.href;
  const statusLabel = t(`status_${order.status}`) || getStatusLabel(order.status);
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-4863">
      <MarketplaceHeader data-id="element-4864" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12" data-id="element-4865">
        <div className="flex items-center justify-between mb-8" data-id="element-4866">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-4867">
            {t('tracking_title')}
          </h1>
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${getStatusColor(order.status)}`} data-id="element-4868">
            {statusLabel}
          </Badge>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-4869">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-4870">
            <div data-id="element-4871">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-4872">
                {t('confirm_order_number')}
              </p>
              <p className="text-2xl font-black text-gray-900" data-id="element-4873">
                #{order.order_number}
              </p>
            </div>
            <div className="text-right" data-id="element-4874">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-4875">
                {t('total')}
              </p>
              <p className="text-2xl font-black text-brand-primary" data-id="element-4876">
                {formatCurrency(order.total)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4877">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-4878">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-4879">
                <Store className="w-5 h-5 text-blue-600" data-id="element-4880" />
              </div>
              <div data-id="element-4881">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-4882">
                  Restaurante
                </h3>
                <p className="font-medium text-gray-900" data-id="element-4883">
                  Pedido del restaurante
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4884">
                  {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-4885">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-4886">
                <Receipt className="w-5 h-5 text-emerald-600" data-id="element-4887" />
              </div>
              <div data-id="element-4888">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-4889">
                  {t('total')}
                </h3>
                <p className="font-bold text-xl text-gray-900" data-id="element-4890">
                  {formatCurrency(order.total)}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4891">
                  Pago:{' '}
                  {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-4892">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4893">
            {t('tracking_title')}
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} data-id="element-4894" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-4895">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4896">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-4897" />
              {t('checkout_address')}
            </h3>
            <p className="text-gray-600" data-id="element-4898">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-4899">
                <span className="font-semibold" data-id="element-4900">Notas:</span> {order.notes}
              </div>}
          </div>}

        {/* Tracking Link */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-4901">
          <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-4902">
            {t('copy_tracking')}
          </p>
          <div className="flex items-center gap-3" data-id="element-4903">
            <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-4904">
              {trackingUrl}
            </div>
            <button onClick={handleCopyLink} className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl border-2 border-gray-200 hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-4905">
              {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-4906" /> : <Copy className="w-5 h-5" data-id="element-4907" />}
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-4908" />
    </div>;
}